// Curated, result-matched fertilizer picks for the fertilizer calculator.
// Instead of a generic "popular fertilizers" block, prescribe the fertilizer
// CLASS that fits the user's grass: starter for new grass, low-burn for the
// low-input grasses (centipede / bahia / buffalo), warm-season food for southern
// grass, balanced slow-release for cool-season. Resolves to the shared catalog
// (data/guide-product-recommendations.ts).
//
// IMPORTANT (agronomy review 2026-06-26): we deliberately DO NOT route any grass
// to fast-release high-nitrogen (ammonium sulfate 21-0-0 / liquid 28-0-0). The
// fertilizer engine's "thicken / fill thin spots" goal maps to applicationGoal
// 'recovery', NOT a heat/drought event — and fast N neither thickens a bunch-type
// lawn (overseeding does) nor is safe on heat-stressed cool-season turf or on
// centipede/bahia (it triggers decline). So goal only special-cases new-seed
// establishment; everything else feeds by grass class. The burn-prone guard runs
// before the warm/cool split so low-input grasses never get a standard feed.

import { GRASS_CHARACTERISTICS, resolveGrassDisplayName } from '@/data/grass-characteristics'
import type { CatalogPick, ProductRecommendationLink } from '@/lib/product-picks/types'
import { resolvePicks } from '@/lib/product-picks/resolve'

type FertKey = 'establishment' | 'low-burn' | 'warm-maintenance' | 'cool-maintenance'

const PICKS: Record<FertKey, CatalogPick[]> = {
  establishment: [
    {
      key: 'starter-scotts',
      reason: 'Starter food — the planting-time phosphorus new seed and sod need to root in.',
    },
    {
      key: 'fertilizer-milorganite',
      reason: 'Once it is up and mowed a few times, a gentle slow-release organic feed that will not burn tender new grass.',
    },
  ],
  'low-burn': [
    {
      key: 'fertilizer-milorganite',
      reason: 'Low-nitrogen, slow-release 6-4-0 — the safe feed for centipede, bahia, and buffalo, which thin out and decline under aggressive nitrogen.',
    },
  ],
  'warm-maintenance': [
    {
      key: 'fertilizer-southern-lawn-food',
      reason: 'Warm-season lawn food for southern grasses through their summer growing window — steady feeding helps bermuda and zoysia spread and thicken.',
    },
    {
      key: 'fertilizer-pgf-complete',
      reason: 'Balanced 16-4-8 slow-release alternative for even feeding without a quick top-growth flush.',
    },
  ],
  'cool-maintenance': [
    {
      key: 'fertilizer-pgf-complete',
      reason: 'Balanced 16-4-8 slow-release — steady feeding for an established cool-season lawn. To fill thin spots in a bunch-type lawn (fescue, rye), overseed first, then feed.',
    },
    {
      key: 'fertilizer-simple-1648',
      reason: 'Liquid 16-4-8 alternative for quick, even feeding on smaller lawns via a hose-end sprayer.',
    },
  ],
}

// Low-input grasses that scorch / decline under standard feeding rates — they
// take a gentle, low-nitrogen program. Buffalograss is a ~1 lb-N/yr native and
// belongs here too (matches the catalog's own guide mapping).
const BURN_PRONE = new Set(['centipede', 'bahiagrass', 'buffalograss'])

function normalizeGrass(grassType: string): string {
  return grassType.toLowerCase().replace(/[^a-z]+/g, ' ').replace(/\s+/g, ' ').trim()
}

function isWarmSeason(grassType: string): boolean {
  return GRASS_CHARACTERISTICS[grassType]?.season === 'Warm'
}

function deriveKey(result: { grassType: string; applicationGoal?: string }): FertKey {
  // New seed/sod always wants starter first, regardless of grass.
  if (result.applicationGoal === 'establishment') return 'establishment'
  // Low-input grasses take a gentle feed for every other goal (never fast N).
  if (BURN_PRONE.has(normalizeGrass(result.grassType))) return 'low-burn'
  if (isWarmSeason(result.grassType)) return 'warm-maintenance'
  return 'cool-maintenance'
}

/**
 * Curated fertilizer picks matched to the result's grass class (and new-seed
 * goal). Never recommends fast-release high-N — see the file header. Always
 * returns at least one pick, so the page only falls back to the generic block
 * before a result exists. Pure.
 */
export function getFertilizerPicks(
  result: { grassType: string; applicationGoal?: string },
): ProductRecommendationLink[] {
  if (resolveGrassDisplayName(result.grassType) === 'Carpetgrass') return []
  return resolvePicks(PICKS[deriveKey(result)])
}
